/**
 * Preview of a content block as the website will render it.
 *
 * It mirrors the site's own look — deep navy ground, gold mono eyebrow, large
 * display heading, dimmed lede — and picks a shape from the data: a hero, a
 * card grid, a table, a stat row, a list. It previews the *draft*, so an admin
 * sees a wording change before saving it.
 *
 * This is a faithful approximation, not the page itself: fonts and exact
 * spacing come from the website's stylesheet. "View on site" opens the real page.
 */
import React from 'react';
import { ExternalLink } from 'lucide-react';

const SITE = 'https://rinterconsultgroup.com';

// Where each page lives on the public site.
export const PAGE_PATHS = {
  global: '/',
  home: '/',
  about: '/about',
  services: '/services',
  projects: '/projects',
  equipment: '/equipment',
  team: '/team',
  companies: '/companies',
  contact: '/contact',
  minet: '/card',
};

const ESCAPES = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' };

// Same contract as the website: escape, then **phrase** becomes bold.
const richText = (value) =>
  String(value ?? '')
    .replace(/[&<>"']/g, (ch) => ESCAPES[ch])
    .replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');

const Rich = ({ value, className }) => (
  <span className={className} dangerouslySetInnerHTML={{ __html: richText(value) }} />
);

const isPlainObject = (v) => !!v && typeof v === 'object' && !Array.isArray(v);
const has = (obj, ...keys) => keys.some((k) => obj[k] !== undefined && obj[k] !== '');

const Eyebrow = ({ children }) => (
  <span className="inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.14em] text-yellow-300">
    <span className="w-[6px] h-[6px] rounded-full bg-yellow-300 flex-shrink-0" />
    {children}
  </span>
);

const Heading = ({ children, small }) => (
  <h3 className={`font-bold text-white leading-tight ${small ? 'text-lg' : 'text-2xl'} mt-2 mb-2`}>
    {children}
  </h3>
);

const Lede = ({ children }) => (
  <p className="text-[13.5px] leading-relaxed text-slate-300/90 max-w-prose">{children}</p>
);

const Datum = ({ children }) => (
  <p className="font-mono text-[11px] uppercase tracking-[0.1em] text-slate-300/80">{children}</p>
);

const Chip = ({ children }) => (
  <span className="font-mono text-[10px] uppercase tracking-[0.1em] text-yellow-300/90 bg-white/5 border border-white/10 rounded px-2 py-0.5">
    {children}
  </span>
);

const Card = ({ children }) => (
  <div className="bg-white/[0.06] border border-white/10 rounded-lg p-4">{children}</div>
);

// ── shapes ───────────────────────────────────────────────────────────────────

const HeroPreview = ({ value }) => (
  <div>
    {value.eyebrow ? <Eyebrow>{value.eyebrow}</Eyebrow> : null}
    <h3 className="font-bold text-white text-3xl leading-[1.12] tracking-tight mt-3 mb-3">
      {value.title}
      {value.title_emphasis ? (
        <>
          <br />
          <em className="font-normal italic text-yellow-100/90">{value.title_emphasis}</em>
        </>
      ) : null}
    </h3>
    {value.lede ? <Lede>{value.lede}</Lede> : null}
    {(value.cta_primary || value.cta_secondary || value.cta || value.button) && (
      <div className="flex flex-wrap gap-2 mt-4">
        {[value.cta_primary, value.cta, value.button].filter(Boolean).map((label) => (
          <span key={label} className="bg-yellow-300 text-[#001f3f] text-xs font-bold rounded-full px-4 py-2">
            {label}
          </span>
        ))}
        {value.cta_secondary ? (
          <span className="border border-white/25 text-white text-xs font-bold rounded-full px-4 py-2">
            {value.cta_secondary}
          </span>
        ) : null}
      </div>
    )}
    {value.datum ? <div className="mt-4"><Datum><Rich value={value.datum} /></Datum></div> : null}
  </div>
);

const SectionPreview = ({ value }) => (
  <div>
    {value.eyebrow ? <Eyebrow>{value.eyebrow}</Eyebrow> : null}
    {(value.heading || value.heading_line2) && (
      <Heading>
        {value.heading}
        {value.heading_line2 ? <><br />{value.heading_line2}</> : null}
      </Heading>
    )}
    {value.lede ? <Lede>{value.lede}</Lede> : null}
    {value.body ? <Lede><Rich value={value.body} /></Lede> : null}
    {value.bio ? (
      <div className="space-y-3 mt-1">
        {String(value.bio).split(/\n\s*\n/).filter(Boolean).map((para, i) => (
          <Lede key={i}><Rich value={para} /></Lede>
        ))}
      </div>
    ) : null}
    {Array.isArray(value.paragraphs) && (
      <div className="space-y-3 mt-2">
        {value.paragraphs.map((para, i) => <Lede key={i}><Rich value={para} /></Lede>)}
      </div>
    )}
    {Array.isArray(value.steps) && (
      <ol className="list-decimal pl-5 space-y-1.5 mt-3 text-[13.5px] text-slate-200">
        {value.steps.map((step, i) => <li key={i}>{step}</li>)}
      </ol>
    )}
    {value.features_heading ? (
      <div className="mt-4"><Eyebrow>{value.features_heading}</Eyebrow></div>
    ) : null}
    {value.name && !value.heading ? <Heading small>{value.name}</Heading> : null}
    {value.role ? <Datum>{String(value.role).toUpperCase()}</Datum> : null}
    {(value.cta || value.button) && (
      <span className="inline-block mt-4 bg-yellow-300 text-[#001f3f] text-xs font-bold rounded-full px-4 py-2">
        {value.cta || value.button}
      </span>
    )}
  </div>
);

const LabelsPreview = ({ value }) => (
  <div className="flex flex-wrap gap-2">
    {Object.entries(value).map(([field, text]) => (
      <Chip key={field}>{String(text)}</Chip>
    ))}
  </div>
);

const StatsPreview = ({ items }) => (
  <div className="grid grid-cols-2 gap-4">
    {items.map((s, i) => (
      <div key={i}>
        <Datum>{s.note}</Datum>
        <p className="text-2xl font-bold text-white leading-tight">{s.value}</p>
        <p className="text-xs text-slate-300/80">{s.label}</p>
      </div>
    ))}
  </div>
);

const TablePreview = ({ items, columns }) => (
  <div className="overflow-x-auto">
    <table className="w-full text-left">
      <thead>
        <tr className="border-b border-white/15">
          {columns.map((c) => (
            <th key={c} className="font-mono text-[10px] uppercase tracking-[0.1em] text-yellow-300/90 py-2 pr-4">
              {c}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {items.slice(0, 6).map((row, i) => (
          <tr key={i} className="border-b border-white/5">
            {columns.map((c) => (
              <td key={c} className="text-[12.5px] text-slate-200 py-2 pr-4 align-top">{String(row[c] ?? '')}</td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
    {items.length > 6 ? (
      <p className="text-[11px] text-slate-400 mt-2">+ {items.length - 6} more rows on the site</p>
    ) : null}
  </div>
);

const CardsPreview = ({ items }) => (
  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
    {items.slice(0, 6).map((item, i) => (
      <Card key={i}>
        {item.period || item.registration || item.year ? (
          <Datum>{item.period || item.registration || `YEAR ${item.year}`}</Datum>
        ) : null}
        <p className="text-sm font-bold text-white mt-1">
          {item.title || item.name || item.heading || item.label}
          {item.meaning ? <span className="text-slate-300/70 font-normal"> · {item.meaning}</span> : null}
        </p>
        {item.location ? <p className="text-xs text-slate-300/80 mt-0.5">{item.location}</p> : null}
        {(item.desc || item.body || item.tagline || item.intro) && (
          <p className="text-[12.5px] text-slate-300/90 mt-1.5 leading-relaxed">
            {item.desc || item.body || item.tagline || item.intro}
          </p>
        )}
        {item.client ? <div className="mt-1.5"><Datum>CLIENT · {item.client}</Datum></div> : null}
        {item.status ? <div className="mt-1.5"><Chip>{item.status}</Chip></div> : null}
      </Card>
    ))}
    {items.length > 6 ? (
      <p className="text-[11px] text-slate-400 sm:col-span-2">+ {items.length - 6} more on the site</p>
    ) : null}
  </div>
);

const BulletsPreview = ({ items }) => (
  <ul className="list-disc pl-5 space-y-1 text-[13.5px] text-slate-200">
    {items.map((item, i) => <li key={i}>{String(item)}</li>)}
  </ul>
);

const FieldsPreview = ({ value }) => (
  <dl className="space-y-2">
    {Object.entries(value).map(([field, fieldValue]) => (
      <div key={field}>
        <dt className="font-mono text-[10px] uppercase tracking-[0.1em] text-yellow-300/90">{field}</dt>
        <dd className="text-[13px] text-slate-200">
          {Array.isArray(fieldValue) ? fieldValue.join(' · ') : String(fieldValue)}
        </dd>
      </div>
    ))}
  </dl>
);

const pickShape = (block, value) => {
  if (value === null || value === undefined) return null;

  if (Array.isArray(value)) {
    if (value.length === 0) return <p className="text-sm text-slate-400">Nothing in this list yet.</p>;
    if (typeof value[0] === 'string') return <BulletsPreview items={value} />;
    const first = value[0] || {};
    if (has(first, 'value') && has(first, 'label')) return <StatsPreview items={value} />;
    if (has(first, 'date') && has(first, 'project')) {
      return <TablePreview items={value} columns={['date', 'project', 'client']} />;
    }
    return <CardsPreview items={value} />;
  }

  if (isPlainObject(value)) {
    if (has(value, 'title') || has(value, 'title_emphasis')) return <HeroPreview value={value} />;
    if (has(value, 'heading', 'lede', 'body', 'paragraphs', 'steps', 'bio', 'eyebrow')) {
      return <SectionPreview value={value} />;
    }
    const allShortStrings = Object.values(value).every(
      (v) => typeof v === 'string' && v.length <= 60,
    );
    if (allShortStrings) return <LabelsPreview value={value} />;
    return <FieldsPreview value={value} />;
  }

  const text = String(value);
  const looksLikeDatum = text === text.toUpperCase() && text.length < 160;
  return looksLikeDatum
    ? <Datum><Rich value={text} /></Datum>
    : <Lede><Rich value={text} /></Lede>;
};

function ContentPreview({ block, value }) {
  const path = PAGE_PATHS[block.page] || '/';

  return (
    <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
      <div className="px-5 py-3 border-b border-slate-100 flex items-center justify-between gap-3">
        <div>
          <p className="text-xs font-bold text-slate-700">Preview</p>
          <p className="text-[11px] text-slate-400">How this reads on the site, before you save</p>
        </div>
        <a href={`${SITE}${path}`} target="_blank" rel="noopener noreferrer"
           className="flex items-center gap-1.5 text-[11px] font-semibold text-teal-700 hover:text-teal-800 flex-shrink-0">
          View on site <ExternalLink size={12} />
        </a>
      </div>

      {/* The site's own ground: deep navy, gold accent, light type. */}
      <div className="p-6" style={{ backgroundColor: '#001527' }}>
        {pickShape(block, value)}
      </div>

      <p className="px-5 py-2.5 text-[11px] text-slate-400 bg-slate-50 border-t border-slate-100">
        Fonts and spacing are close, not exact — the site's own stylesheet decides those.
      </p>
    </div>
  );
}

export default ContentPreview;
